import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Instagram, MapPin, Star } from "lucide-react";
import { IMG, LINKS } from "../data/content";
import { CONVERSIONS, reportConversion } from "../utils/gtag";
import { scrollTo } from "./Nav";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMG.neon}
          alt="The neon 'Brew It Up' wall inside Brewing Bistro, Baner"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-espresso/40" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-espresso/80 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-espresso via-espresso/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-espresso/80 via-espresso/20 to-transparent" />
      <div className="animate-neon pointer-events-none absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-neon/15 blur-[140px]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-40 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-5 font-mono text-[11px] uppercase tracking-[0.35em] text-neon"
          data-testid="hero-eyebrow"
        >
          Cafe &amp; Eatery — Baner, Pune
        </motion.p>

        <h1 className="max-w-3xl font-serif text-5xl leading-[1.04] tracking-tight text-cream sm:text-6xl lg:text-7xl" data-testid="hero-headline">
          <MaskedLine delay={0.35}>Brew it up,</MaskedLine>
          <MaskedLine delay={0.5}>
            the <em className="italic text-neon">Baner</em> way.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-sage sm:text-lg"
          data-testid="hero-subtext"
        >
          Emerald velvet corners, a warm neon glow, Neapolitan pizzas and coffee that actually gets you.
          The table everyone wishes they reserved is waiting — walk in.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href={LINKS.maps}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-get-directions-btn"
            onClick={() => reportConversion(CONVERSIONS.directions)}
            className="group flex items-center gap-2.5 rounded-full bg-neon px-7 py-3.5 font-medium text-espresso shadow-[0_0_40px_rgba(255,184,0,0.35)] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_60px_rgba(255,184,0,0.5)]"
          >
            <MapPin size={17} className="transition-transform duration-300 group-hover:-translate-y-0.5" />
            Get Directions
          </a>
          <a
            href={LINKS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-instagram-btn"
            className="group flex items-center gap-2.5 rounded-full border border-cream/25 px-7 py-3.5 font-medium text-cream backdrop-blur-sm transition-all duration-300 hover:border-neon/60 hover:text-neon"
          >
            <Instagram size={17} className="transition-transform duration-300 group-hover:rotate-12" />
            Stalk us first
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-cream/10 pt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-mist"
          data-testid="hero-stats"
        >
          <span className="flex items-center gap-2">
            <Star size={13} className="fill-[#FFB800] text-neon" /> 4.7 · 622 dining ratings
          </span>
          <span>Open today till 11:30 PM</span>
          <span>₹700 for two</span>
        </motion.div>
      </motion.div>

      <motion.a
        href="#vibe"
        onClick={(e) => scrollTo(e, "#vibe")}
        data-testid="hero-scroll-indicator"
        style={{ opacity: fade }}
        className="absolute bottom-6 right-6 z-10 hidden text-sage sm:block"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        aria-label="Scroll to the vibe section"
      >
        <ArrowDown size={20} />
      </motion.a>
    </section>
  );
}
